import {
    EmailOutlined,
    FactCheckOutlined,
    LocalShippingOutlined,
    RecordVoiceOverOutlined,
    SchoolOutlined,
    StoreOutlined,
    SupportAgentOutlined,
} from '@mui/icons-material';
import QuestionMark from '@mui/icons-material/QuestionMark';
import { Box, Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { BsWhatsapp } from 'react-icons/bs';

import { HeaderIconButton } from 'components/header/HeaderIconButton';
import { useClientContext } from 'context/ClientContext';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useDeskManager } from 'hooks/useDeskManager';
import { useQueryWhatsApp } from 'hooks/useQueryWhatsApp';
import { useRequestListagem } from 'hooks/useRequestListagem';

export const taskBarIcons = {
    school_icon: <SchoolOutlined />,
    fact_check_icon: <FactCheckOutlined />,
    record_voice_over_icon: <RecordVoiceOverOutlined />,
    email_icon: <EmailOutlined />,
    local_shipping_icon: <LocalShippingOutlined />,
    store_icon: <StoreOutlined />,
} as const;

export type AcessoRapido = {
    dsAcessoRapido: string;
    dsIcone: string;
    fgAtivo: boolean;
    idAcessoRapido: number;
    lkAcessoRapido: string;
    nrOrdem: number;
};

export const useQueryAcessoRapido = () => {
    const requestListagem = useRequestListagem();

    return useQuery<AcessoRapido[]>({
        queryKey: ['acesso-rapido'],
        queryFn: () =>
            requestListagem({
                url: '/gestao/acessorapido',
                sort: [{ property: 'nrOrdem', direction: 'ASC' }],
            }).then((response) => response?.data.data),
    });
};

export function MenuShortcuts() {
    const { fgExibeHelpDesk } = useClientContext();
    const { openDeskManager, isFetching: isFetchingDeskManager } = useDeskManager();
    const { data: link } = useQueryWhatsApp();
    const { isUpMd } = useBreakpoints();
    const { data = [], isFetching: isFetchingAcessoRapido } = useQueryAcessoRapido();
    const buttons = data.filter((acessoRapido) => acessoRapido.fgAtivo === true);

    if (!isUpMd && link)
        return (
            <Tooltip title="Fale com o Customer">
                <HeaderIconButton onClick={() => window.open(link, '_blank')}>
                    <BsWhatsapp />
                </HeaderIconButton>
            </Tooltip>
        );

    if (!isUpMd && !fgExibeHelpDesk) return null;

    if (isUpMd && !isFetchingAcessoRapido && !buttons?.length && !fgExibeHelpDesk) return null;

    return (
        <Box
            sx={{
                height: 40,
                display: 'flex',
                alignItems: 'center',
                border: 1,
                borderRadius: 1.5,
                borderColor: (theme) => theme.lighten(theme.palette.secondary.main, 0.2),
                backgroundColor: (theme) => theme.lighten(theme.palette.secondary.main, 0.075),
                color: 'common.white',
                overflow: 'hidden',
            }}
        >
            {!isFetchingAcessoRapido &&
                isUpMd &&
                buttons?.map((acessoRapido, index) => (
                    <Tooltip key={index} title={acessoRapido.dsAcessoRapido}>
                        <HeaderIconButton
                            onClick={() => window.open(acessoRapido.lkAcessoRapido, '_blank')?.focus()}
                            sx={{ border: 'none', borderRadius: 0 }}
                        >
                            {taskBarIcons[acessoRapido.dsIcone as keyof typeof taskBarIcons] ?? <QuestionMark />}
                        </HeaderIconButton>
                    </Tooltip>
                ))}

            {isUpMd && fgExibeHelpDesk && (
                <Tooltip title="Suporte">
                    <HeaderIconButton sx={{ border: 'none', borderRadius: 0 }} loading={isFetchingDeskManager} onClick={openDeskManager}>
                        <SupportAgentOutlined />
                    </HeaderIconButton>
                </Tooltip>
            )}
        </Box>
    );
}
